import { useState, useEffect, useRef } from 'react'
import { supabase, uploadFile, getFileUrl } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import {
  CheckCircle2, X, Upload, FileText, Lock, AlertCircle, Loader, Info, Sparkles, ExternalLink,
} from 'lucide-react'

// ── DRL simplificado para usuarios Caja ────────────────────────────────
// Lista guiada con 3 acciones rápidas por ítem:
//   ✓ "Ya lo tengo"   → marca Recibido (sin upload)
//   ⬆ "Subir archivo" → upload directo a Supabase Storage + marca Recibido
//   ✕ "No lo tengo"   → marca No existe (engagement: dice qué le falta sin abandonar)
//
// En modo trial: límite de 5 archivos subidos. Al intentar el 6to, dispara
// el paywall (onLimitReached callback). El estado se persiste en
// solicitudes_informacion + archivo_url + notas.
// ────────────────────────────────────────────────────────────────────────

const TRIAL_UPLOAD_LIMIT = 5

const CAT_META = {
  'Corporativo':         { icon: '🎯', tint: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200' },
  'Unidades de negocio': { icon: '🏢', tint: 'bg-slate-50',   text: 'text-slate-700',   border: 'border-slate-200' },
  'Financiero':          { icon: '💰', tint: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  'Comercial':           { icon: '📈', tint: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200' },
  'Operaciones':         { icon: '⚙️', tint: 'bg-gray-50',    text: 'text-gray-700',    border: 'border-gray-200' },
  'Marketing':           { icon: '📣', tint: 'bg-pink-50',    text: 'text-pink-700',    border: 'border-pink-200' },
  'RRHH':                { icon: '👥', tint: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
}

export default function CajaDRL({ consultancyId, onLimitReached, isTrial = true }) {
  const { profile } = useAuth()
  const [items, setItems]         = useState([])
  const [loading, setLoading]     = useState(true)
  const [uploading, setUploading] = useState(null) // id del ítem que está subiendo
  const [error, setError]         = useState('')
  const fileInputRef              = useRef(null)
  const [activeItemId, setActiveItemId] = useState(null)

  useEffect(() => { if (consultancyId) load() }, [consultancyId])

  async function load() {
    setLoading(true)
    const { data } = await supabase
      .from('solicitudes_informacion')
      .select('*')
      .eq('consultancy_id', consultancyId)
      .order('orden')
    setItems(data ?? [])
    setLoading(false)
  }

  // ── Conteo de uploads (para trial limit) ───────────────────────────────
  const uploadedCount = items.filter(i => i.archivo_url).length
  const trialAtLimit  = isTrial && uploadedCount >= TRIAL_UPLOAD_LIMIT

  // ── Mark "Ya lo tengo" ─────────────────────────────────────────────────
  async function markRecibido(id) {
    await supabase.from('solicitudes_informacion')
      .update({ estado: 'Recibido', updated_at: new Date().toISOString() })
      .eq('id', id)
    setItems(prev => prev.map(i => i.id === id ? { ...i, estado: 'Recibido' } : i))
  }

  // ── Mark "No lo tengo" ─────────────────────────────────────────────────
  async function markNoExiste(id) {
    await supabase.from('solicitudes_informacion')
      .update({ estado: 'No existe', archivo_url: null, updated_at: new Date().toISOString() })
      .eq('id', id)
    setItems(prev => prev.map(i => i.id === id ? { ...i, estado: 'No existe', archivo_url: null } : i))
  }

  // ── Reset a pendiente ──────────────────────────────────────────────────
  async function resetPending(id) {
    await supabase.from('solicitudes_informacion')
      .update({ estado: 'Pendiente', archivo_url: null, updated_at: new Date().toISOString() })
      .eq('id', id)
    setItems(prev => prev.map(i => i.id === id ? { ...i, estado: 'Pendiente', archivo_url: null } : i))
  }

  // ── Upload archivo ─────────────────────────────────────────────────────
  function triggerUpload(id) {
    if (trialAtLimit) {
      onLimitReached?.()
      return
    }
    setActiveItemId(id)
    fileInputRef.current?.click()
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !activeItemId) return
    const item = items.find(i => i.id === activeItemId)
    if (!item) return

    setUploading(activeItemId)
    setError('')
    try {
      // Volver a chequear el límite (defensivo)
      if (isTrial && uploadedCount >= TRIAL_UPLOAD_LIMIT) {
        onLimitReached?.()
        return
      }
      const safeName = file.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '')
      const path = `drl/${consultancyId}/${Date.now()}_${safeName}`
      await uploadFile(file, path)
      const url = await getFileUrl(path)
      await supabase.from('solicitudes_informacion')
        .update({
          estado: 'Recibido',
          archivo_url: path,
          notas: file.name,
          updated_at: new Date().toISOString(),
        })
        .eq('id', activeItemId)
      setItems(prev => prev.map(i =>
        i.id === activeItemId
          ? { ...i, estado: 'Recibido', archivo_url: path, notas: file.name }
          : i
      ))
    } catch (err) {
      setError(err.message ?? 'No se pudo subir el archivo')
    } finally {
      setUploading(null)
      setActiveItemId(null)
    }
  }

  // ── Métricas ───────────────────────────────────────────────────────────
  const total      = items.length
  const respondido = items.filter(i =>
    i.estado === 'Recibido' || i.estado === 'No aplica' || i.estado === 'No existe'
  ).length
  const pct = total > 0 ? Math.round((respondido / total) * 100) : 0

  // ── Agrupar por categoría ───────────────────────────────────────────────
  const byCategory = items.reduce((acc, item) => {
    if (!acc[item.categoria]) acc[item.categoria] = []
    acc[item.categoria].push(item)
    return acc
  }, {})
  const categories = Object.keys(byCategory)

  if (loading) return (
    <div className="flex items-center justify-center py-10">
      <Loader size={20} className="animate-spin text-brand-500" />
    </div>
  )

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.xlsx,.xls,.docx,.doc,.csv,.png,.jpg,.jpeg"
        onChange={handleFileChange}
      />

      {/* Header explicativo */}
      <div className="rounded-xl bg-gradient-to-r from-brand-800 to-brand-700 px-5 py-4 text-white">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <FileText size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-base">Información que vamos a necesitar</h2>
            <p className="text-cream-100/80 text-xs mt-1 leading-relaxed max-w-2xl">
              Esta es la lista de documentos típicos que la metodología NVC va a usar.
              Por cada uno indica si <strong>ya lo tienes</strong>, si quieres <strong>subirlo</strong>
              {' '}para que la IA lo lea, o si <strong>no existe</strong> en tu empresa todavía.
              No necesitas tener todo — sirve para mapear qué hay y qué falta.
            </p>
          </div>
        </div>
      </div>

      {/* Progreso */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Progreso</span>
            <span className="text-xs tabular-nums text-gray-700 font-semibold">
              {respondido} de {total} respondidos
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {isTrial && (
          <div className={`px-3 py-2 rounded-xl text-xs font-semibold border whitespace-nowrap ${
            trialAtLimit
              ? 'bg-red-50 border-red-200 text-red-700'
              : uploadedCount >= 3
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-blue-50 border-blue-200 text-blue-700'
          }`}>
            {uploadedCount}/{TRIAL_UPLOAD_LIMIT} archivos en trial
            {trialAtLimit && ' · Límite alcanzado'}
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Lista por categoría */}
      {categories.map(cat => {
        const meta    = CAT_META[cat] ?? CAT_META['Operaciones']
        const catItems = byCategory[cat]
        return (
          <div key={cat} className="space-y-2">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${meta.tint} ${meta.border} border`}>
              <span className="text-base">{meta.icon}</span>
              <h3 className={`text-sm font-semibold ${meta.text}`}>{cat}</h3>
              <span className={`ml-auto text-[11px] font-bold tabular-nums ${meta.text}`}>
                {catItems.filter(i => i.estado === 'Recibido' || i.estado === 'No aplica' || i.estado === 'No existe').length}
                /{catItems.length}
              </span>
            </div>
            <div className="space-y-1.5">
              {catItems.map(item => (
                <ItemRow
                  key={item.id}
                  item={item}
                  uploading={uploading === item.id}
                  trialAtLimit={trialAtLimit}
                  onMarkRecibido={() => markRecibido(item.id)}
                  onMarkNoExiste={() => markNoExiste(item.id)}
                  onUpload={() => triggerUpload(item.id)}
                  onReset={() => resetPending(item.id)}
                />
              ))}
            </div>
          </div>
        )
      })}

      {/* Footer hint */}
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2.5">
        <Info size={13} className="flex-shrink-0 mt-0.5" />
        <span className="leading-relaxed">
          Subir archivos es opcional pero ayuda a la IA a darte un mejor diagnóstico.
          {isTrial && (
            <> En modo trial puedes subir hasta {TRIAL_UPLOAD_LIMIT} archivos
              ({TRIAL_UPLOAD_LIMIT - uploadedCount} restantes).
              Suscríbete para subir todos los que necesites.</>
          )}
        </span>
      </div>
    </div>
  )
}

// ── ItemRow ────────────────────────────────────────────────────────────────────
function ItemRow({ item, uploading, trialAtLimit, onMarkRecibido, onMarkNoExiste, onUpload, onReset }) {
  const respondido = item.estado === 'Recibido' || item.estado === 'No aplica' || item.estado === 'No existe'
  const hasFile    = !!item.archivo_url

  // Estado visual
  const stateBadge = {
    'Recibido':   { bg: 'bg-emerald-50',  text: 'text-emerald-700', label: hasFile ? '✓ Archivo subido' : '✓ Ya lo tengo' },
    'No aplica':  { bg: 'bg-gray-50',     text: 'text-gray-600',    label: 'No aplica' },
    'No existe':  { bg: 'bg-orange-50',   text: 'text-orange-700',  label: 'No existe aún' },
    'En revisión':{ bg: 'bg-blue-50',     text: 'text-blue-700',    label: 'En revisión' },
  }[item.estado]

  return (
    <div className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
      respondido ? 'bg-white border-gray-100' : 'bg-white border-gray-200 hover:border-brand-200'
    }`}>
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${respondido ? 'text-gray-600' : 'text-gray-800'}`}>
          {item.descripcion}
        </p>
        {hasFile && item.notas && (
          <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
            <FileText size={11} /> {item.notas}
          </p>
        )}
        {stateBadge && (
          <span className={`inline-block text-[10px] font-semibold mt-1 px-2 py-0.5 rounded-full ${stateBadge.bg} ${stateBadge.text}`}>
            {stateBadge.label}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        {respondido ? (
          <button
            onClick={onReset}
            className="text-[11px] text-gray-400 hover:text-gray-700 px-2 py-1 rounded transition-colors"
            title="Cambiar respuesta"
          >
            Cambiar
          </button>
        ) : (
          <>
            <button
              onClick={onMarkRecibido}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded transition-colors border border-emerald-200"
            >
              <CheckCircle2 size={11} /> Ya lo tengo
            </button>
            <button
              onClick={onUpload}
              disabled={uploading || trialAtLimit}
              title={trialAtLimit ? 'Límite del trial alcanzado · Suscríbete para subir más' : 'Subir archivo'}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded transition-colors border border-blue-200"
            >
              {uploading
                ? <Loader size={11} className="animate-spin" />
                : trialAtLimit
                  ? <Lock size={11} />
                  : <Upload size={11} />}
              Subir
            </button>
            <button
              onClick={onMarkNoExiste}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 hover:bg-orange-50 px-2 py-1 rounded transition-colors border border-orange-200"
            >
              <X size={11} /> No lo tengo
            </button>
          </>
        )}
      </div>
    </div>
  )
}
